import { MaterialCommunityIcons } from "@expo/vector-icons";
import type { ReactNode } from "react";
import { Platform, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { colors, fonts } from "@/lib/theme";

const ROSE = "#E11D48";
const ROSE_OUTER_GLOW = "rgba(225,29,72,0.08)";

interface Props {
    /** The question text, bold ink. */
    label: string;
    /** Optional small helper line between the label and the control. */
    hint?: string;
    /** When true, renders a red asterisk after the label. */
    required?: boolean;
    /** Optional inline error line under the control (required-field message). */
    error?: string | null;
    /**
     * When true (any unresolved validation error attached), the card paints a
     * red 1.5px border + a soft outer glow shadow. Matches the design's
     * `flagged` QCard state.
     */
    flagged?: boolean;
    /** The question control (Radio / Check / YesNo / Number / etc.). */
    children: ReactNode;
    /**
     * Optional slot for full-bleed children (e.g. the ValidationErrorCard
     * strip) that hug the card's edges and divide from the padded content via
     * the strip's own borderTop hairline. Rendered AFTER the padded content.
     */
    errorSlot?: ReactNode;
}

/**
 * Flat-section question row. Wraps each question in a white rounded card with
 * a 1.5px border (line by default, rose-700 when `flagged`). The padded inner
 * region holds the label / hint / control / required-error line. The optional
 * `errorSlot` renders edge-to-edge inside the card so the validation strip's
 * borderTop divides it cleanly from the padded section above.
 */
export function SpecRow({ label, hint, required, error, flagged, children, errorSlot }: Props) {
    // Flat row — used by every UN-flagged question to keep the historical
    // open-air layout (label + control with no surrounding card).
    if (!flagged && !errorSlot) {
        return (
            <View>
                <Text style={[styles.label, { marginBottom: hint ? 4 : 12 }]}>
                    {label}
                    {required ? <Text style={styles.required}> *</Text> : null}
                </Text>
                {hint ? <Text style={styles.hint}>{hint}</Text> : null}
                {children}
                {error ? (
                    <View style={styles.errorRow}>
                        <MaterialCommunityIcons
                            name="alert-circle-outline"
                            size={13}
                            color={colors.error500}
                        />
                        <Text style={styles.errorText}>{error}</Text>
                    </View>
                ) : null}
            </View>
        );
    }

    // Carded row — used when the question carries an unresolved validation
    // error (or a required-field error). White rounded surface with a 1.5px
    // line border (rose-700 + outer glow when `flagged`). The errorSlot
    // renders edge-to-edge inside the card, divided from the padded section
    // above by its own borderTop hairline.
    return (
        <View style={[styles.card, flagged ? styles.cardFlagged : null]}>
            <View style={styles.padded}>
                <Text style={[styles.label, { marginBottom: hint ? 4 : 12 }]}>
                    {label}
                    {required ? <Text style={styles.required}> *</Text> : null}
                </Text>
                {hint ? <Text style={styles.hint}>{hint}</Text> : null}
                {children}
                {error ? (
                    <View style={styles.errorRow}>
                        <MaterialCommunityIcons
                            name="alert-circle-outline"
                            size={13}
                            color={colors.error500}
                        />
                        <Text style={styles.errorText}>{error}</Text>
                    </View>
                ) : null}
            </View>
            {errorSlot}
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        backgroundColor: "#fff",
        borderWidth: 1.5,
        borderColor: colors.border,
        borderRadius: 16,
        overflow: "hidden"
    },
    cardFlagged: {
        borderColor: ROSE,
        // The design uses a 4px outer ring at 0.08 alpha. iOS shadow params
        // approximate that ring; Android renders the static red border only.
        ...Platform.select({
            ios: {
                shadowColor: ROSE_OUTER_GLOW,
                shadowOffset: { width: 0, height: 0 },
                shadowOpacity: 1,
                shadowRadius: 4
            },
            android: { elevation: 2 },
            default: {}
        })
    },
    padded: {
        paddingVertical: 14,
        paddingHorizontal: 16
    },
    label: {
        fontFamily: fonts.bold,
        fontSize: 16,
        color: colors.ink,
        letterSpacing: -0.2,
        lineHeight: 22
    },
    required: {
        color: colors.error500
    },
    hint: {
        fontFamily: fonts.medium,
        fontSize: 12,
        color: colors.textSecondary,
        lineHeight: 17,
        marginBottom: 12
    },
    errorRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        marginTop: 8
    },
    errorText: {
        flex: 1,
        fontFamily: fonts.bold,
        fontSize: 11.5,
        color: colors.error500
    }
});
